/**
 * 68.41, layout C at the table: the command card (ui/commandLayout.ts). On the right over the corner map, twelve buttons
 * that follow the selection — squads chosen: F's entries (their orders); nothing chosen: G's (the side's calls). A button
 * with ▸ turns the card to its list (Back, Esc, or an order given returns); the card keeps its size. An entry given
 * without a place goes at once; one that needs a place is armed — it rides the cursor until a click on the ground gives
 * it there (Shift keeps it for another place; Esc or the right button drops it). The buttons' keys are CARD_KEYS
 * (T Y U I / H J N P / V B X Z).
 *
 * The card only says what was pressed (`press` → an action); the host gives the order (the same `verbInputs` and `issue`
 * as the ring). DOM: the host sets it each frame at the table — its HTML built and compared, written only on a change —
 * and a label at the cursor while armed.
 */
import { iconSvg } from './commandIcons';
import { CARD_KEYS, needsPlace, type CommandEntry, type CommandWedge } from './commandLayout';

/** What a press did: an entry to give now or to arm, or the card turned a page. */
export type CardAction = { kind: 'give'; entry: CommandEntry } | { kind: 'arm'; entry: CommandEntry } | { kind: 'page' } | null;

/** A slot of the card: a wedge (its own entry, or ▸ its list), an entry of a page, or Back. */
type Slot = { kind: 'wedge'; w: CommandWedge } | { kind: 'entry'; x: CommandEntry } | { kind: 'back' };

export const CARD_ROWS = 3, CARD_COLS = 4;

export class CommandCard {
  private wedges: readonly CommandWedge[] = [];
  private title = '';
  private page: CommandWedge | null = null;
  armed: CommandEntry | null = null;
  private badges: ReadonlyMap<string, string> = new Map();
  private dim: ReadonlySet<string> = new Set();
  private el: HTMLDivElement | null = null;
  private tag: HTMLDivElement | null = null;
  private drawn = '';
  /** The host's handler for a button clicked (the keys come through `pressKey`). */
  onAction: ((a: CardAction) => void) | null = null;

  constructor() {
    if (typeof document === 'undefined') return;
    this.el = document.createElement('div');
    this.el.style.cssText = 'position:fixed; right:14px; bottom:226px; width:316px; background:rgba(12,12,24,0.92); border:1px solid rgba(255,255,255,0.15); border-radius:8px; padding:8px; color:#e8eaf0; font:12px system-ui, sans-serif; z-index:22; display:none; user-select:none;';
    this.el.addEventListener('click', ev => {
      const b = (ev.target as HTMLElement).closest('[data-slot]') as HTMLElement | null;
      if (!b) return;
      ev.stopPropagation();
      this.onAction?.(this.press(Number(b.dataset.slot)));
    });
    this.tag = document.createElement('div');
    this.tag.style.cssText = 'position:fixed; left:0; top:0; pointer-events:none; z-index:31; display:none; background:rgba(12,12,24,0.88); border:1px solid rgba(230,208,112,0.7); border-radius:6px; padding:3px 7px; color:#f0e6b0; font:11px system-ui, sans-serif; white-space:nowrap;';
    document.body.append(this.el, this.tag);
  }

  dispose(): void { this.el?.remove(); this.tag?.remove(); this.el = null; this.tag = null; }

  /** This frame's entries (a new set turns back to the first page), the card's title, counts and dimmed wedges. */
  set(wedges: readonly CommandWedge[], title: string, badges: ReadonlyMap<string, string> = new Map(), dim: ReadonlySet<string> = new Set()): void {
    if (wedges !== this.wedges) { this.wedges = wedges; this.page = null; }
    this.title = title; this.badges = badges; this.dim = dim;
    this.draw();
  }

  /** The card's slots now, in key order. */
  slots(): Slot[] {
    if (this.page?.list) return [...this.page.list.map(x => ({ kind: 'entry' as const, x })), { kind: 'back' as const }];
    return this.wedges.map(w => ({ kind: 'wedge' as const, w }));
  }

  /** A button pressed (by its slot): a page turned, or an entry to give or arm. */
  press(i: number): CardAction {
    const s = this.slots()[i];
    if (!s) return null;
    if (s.kind === 'back') { this.page = null; this.draw(); return { kind: 'page' }; }
    if (s.kind === 'wedge') {
      if (this.dim.has(s.w.id)) return null;
      if (s.w.list) { this.page = s.w; this.armed = null; this.draw(); return { kind: 'page' }; }
      return this.choose(s.w.entry!);
    }
    return this.choose(s.x);
  }

  /** A card key (CARD_KEYS) pressed: its button, or null when it is not one of them. */
  pressKey(key: string): CardAction | undefined {
    const i = (CARD_KEYS as readonly string[]).indexOf(key);
    return i < 0 ? undefined : this.press(i);
  }

  private choose(x: CommandEntry): CardAction {
    if (!needsPlace(x)) { this.armed = null; this.page = null; this.draw(); return { kind: 'give', entry: x }; }   // (given: back to the first page)
    this.armed = x; this.draw();
    return { kind: 'arm', entry: x };
  }

  /** Esc: drop the armed order, else back from a page. True when the card took it. */
  back(): boolean {
    if (this.armed) { this.armed = null; this.draw(); this.cursor(null); return true; }
    if (this.page) { this.page = null; this.draw(); return true; }
    return false;
  }

  /** The armed order given at a place: kept for the next (Shift) or dropped. */
  given(keep: boolean): void { if (!keep) { this.armed = null; this.page = null; this.draw(); this.cursor(null); } }

  /** Where the cursor is (the armed order's label beside it); null hides the label. */
  cursor(at: { x: number; y: number } | null): void {
    if (!this.tag) return;
    if (!at || !this.armed) { this.tag.style.display = 'none'; return; }
    this.tag.style.display = '';
    this.tag.style.left = `${at.x + 16}px`; this.tag.style.top = `${at.y + 14}px`;
    const html = `${iconSvg(this.armed.icon, { size: 13 })} ${this.armed.label} — click the place · Shift keeps · Esc drops`;
    if (this.tag.dataset.html !== html) { this.tag.dataset.html = html; this.tag.innerHTML = html; }
  }

  show(on: boolean): void {
    if (this.el) this.el.style.display = on ? '' : 'none';
    if (!on) { this.armed = null; this.page = null; this.cursor(null); }
  }

  private draw(): void {
    if (!this.el) return;
    const slots = this.slots(), keys = CARD_KEYS;
    const cell = (i: number): string => {
      const s = slots[i];
      if (!s) return `<div style="height:52px;"></div>`;
      const k = keys[i].toUpperCase();
      let label: string, icon: string, dim = false, lit = false, badge: string | undefined;
      if (s.kind === 'back') { label = '◂ Back'; icon = ''; }
      else if (s.kind === 'wedge') { label = s.w.label + (s.w.list ? ' ▸' : ''); icon = iconSvg(s.w.icon, { size: 20 }); dim = this.dim.has(s.w.id); badge = s.w.entry ? this.badges.get(`${s.w.entry.verb}/${s.w.entry.sub}`) : undefined; lit = !!this.armed && s.w.entry === this.armed; }
      else { label = s.x.label; icon = iconSvg(s.x.icon, { size: 20 }); badge = this.badges.get(`${s.x.verb}/${s.x.sub}`); lit = this.armed === s.x; }
      return `<div data-slot="${i}" style="position:relative; height:52px; border-radius:6px; border:1px solid ${lit ? 'rgba(230,208,112,0.8)' : 'rgba(255,255,255,0.12)'}; background:${lit ? 'rgba(80,72,30,0.6)' : 'rgba(28,30,48,0.9)'}; opacity:${dim ? 0.35 : 1}; cursor:${dim ? 'default' : 'pointer'}; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:2px; text-align:center;">`
        + `<span style="position:absolute; left:4px; top:2px; font-size:9px; color:#8a92a4;">${k}</span>${badge ? `<span style="position:absolute; right:4px; top:2px; font-size:9px; color:#e6d070;">${badge}</span>` : ''}`
        + `${icon}<span style="font-size:${label.length > 14 ? 8.5 : 10}px; line-height:1.05; padding:0 2px;">${label}</span></div>`;
    };
    const head = this.page ? `${this.title} ▸ ${this.page.label}` : this.title;
    const html = `<div style="font-size:11px; color:#c8ccd8; margin:0 2px 6px;">${head}</div><div style="display:grid; grid-template-columns:repeat(${CARD_COLS}, 1fr); gap:5px;">${Array.from({ length: CARD_ROWS * CARD_COLS }, (_, i) => cell(i)).join('')}</div>`;
    if (html !== this.drawn) { this.drawn = html; this.el.innerHTML = html; }
  }
}
